import { create } from '@thoughtspot/logger';
import { Alert, } from '@thoughtspot/radiant-react/widgets/alert';
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef, } from 'react';
import ReactDOM from 'react-dom';
import { Action, GLOBAL_ALERT_CONTAINER_ID, GlobalAlertTestId, initialState, V1_GLOBAL_ALERT_CONTAINER_ID, } from './global-alert-context.util';
export const GlobalAlertContext = createContext(null);
const logger = create('Global alert context');
export const reducer = (state, action) => {
    switch (action.type) {
        case Action.UPDATE_BANNER: {
            if (state.currentBannerId !== action.id &&
                action.alertProps.isVisible !== true) {
                return state;
            }
            return {
                ...state,
                bannerAlertProps: action.alertProps.isVisible
                    ? action.alertProps
                    : { ...initialState.bannerAlertProps },
                currentBannerId: action.alertProps.isVisible ? action.id : null,
            };
        }
        case Action.UPDATE_TOAST: {
            if (action.alertProps.isVisible !== true &&
                state.currentToastId &&
                action.id !== state.currentToastId) {
                return state;
            }
            return {
                ...state,
                toastAlertProps: action.alertProps.isVisible
                    ? action.alertProps
                    : { ...initialState.toastAlertProps },
                currentToastId: action.alertProps.isVisible ? action.id : null,
            };
        }
        default:
            return state;
    }
};
export const GlobalAlertContextProvider = ({ children, customBannerAlertContainerId, }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const stateRef = useRef(state);
    stateRef.current = state;
    const updateBannerAlert = useCallback((id, alertProps) => {
        const cur = stateRef.current;
        if (cur.currentBannerId && id !== cur.currentBannerId) {
            if (alertProps.isVisible) {
                cur.bannerAlertProps.onClose();
            }
            else {
                return;
            }
        }
        dispatch({
            type: Action.UPDATE_BANNER,
            alertProps: { ...alertProps, global: true },
            id,
        });
    }, []);
    const showToastWithNewAnimation = useCallback((id, alertProps) => {
        dispatch({
            type: Action.UPDATE_TOAST,
            alertProps: { ...alertProps, isVisible: false },
            id: stateRef.current.currentToastId || id,
        });
        setTimeout(() => {
            dispatch({
                type: Action.UPDATE_TOAST,
                alertProps,
                id,
            });
        });
    }, []);
    const updateToastAlert = useCallback((id, alertProps) => {
        const cur = stateRef.current;
        const shouldRefreshToastAnimation = alertProps.isVisible &&
            cur.toastAlertProps.isVisible &&
            cur.toastAlertProps.message !== alertProps.message &&
            alertProps.shouldRefreshToastAnimation;
        if (shouldRefreshToastAnimation) {
            if (cur.currentToastId &&
                cur.currentToastId !== id &&
                cur.toastAlertProps.onClose) {
                cur.toastAlertProps.onClose();
            }
            showToastWithNewAnimation(id, alertProps);
        }
        else {
            dispatch({
                type: Action.UPDATE_TOAST,
                alertProps,
                id,
            });
        }
    }, [showToastWithNewAnimation]);
    const contextValue = useMemo(() => ({
        updateBannerAlert,
        updateToastAlert,
    }), [updateBannerAlert, updateToastAlert]);
    let bannerAlertPortalContainer;
    if (customBannerAlertContainerId) {
        bannerAlertPortalContainer = document.getElementById(customBannerAlertContainerId);
    }
    else {
        bannerAlertPortalContainer = document.getElementById(V1_GLOBAL_ALERT_CONTAINER_ID);
        if (!bannerAlertPortalContainer) {
            bannerAlertPortalContainer = document.getElementById(GLOBAL_ALERT_CONTAINER_ID);
        }
    }
    const globalBannerAlert = bannerAlertPortalContainer
        ? ReactDOM.createPortal(React.createElement(Alert.Banner, { ...state.bannerAlertProps, "data-testid": GlobalAlertTestId.BANNER }), bannerAlertPortalContainer)
        : null;
    const globalToastAlert = ReactDOM.createPortal(React.createElement(Alert.Toast, { ...state.toastAlertProps, "data-testid": GlobalAlertTestId.TOAST }), document.body);
    useEffect(() => {
        return () => {
            if (!bannerAlertPortalContainer &&
                state.bannerAlertProps.isVisible) {
                logger.warn(`Banner alert is not shown because there is no container found. Looked for the following containers (in order of precedence):\n` +
                    `1. Custom container id: ${customBannerAlertContainerId}\n` +
                    `2. Container id in Blink v1 shell: ${V1_GLOBAL_ALERT_CONTAINER_ID}\n` +
                    `3, Container id in Blink v2 shell: ${GLOBAL_ALERT_CONTAINER_ID}\n`);
            }
        };
    });
    return (React.createElement(React.Fragment, null,
        globalBannerAlert,
        globalToastAlert,
        React.createElement(GlobalAlertContext.Provider, { value: contextValue }, children)));
};
//# sourceMappingURL=global-alert-context.js.map